import React from "react";
import {
  BLEED, C, FONT, DataCenter, Worker, clamp, lerp, seg, span, smooth, mixColor, mixPose, rng,
} from "../kit";

// Act VI · The Working Man (the last chorus and the outro: the elegy).
//
//   p 0 – 0.21    The field at dusk. A plain pine coffin on two sawhorses, his
//                 hammer laid on the lid. A SOLD sign leans out in the field,
//                 SITE 7 glows on the horizon, and the tomato vines in front
//                 hang heavy with fruit gone bad. Wind in the grass, fireflies.
//   p 0.21 – 0.5  The roll call. Trades stamped one by one onto the OUT rack,
//                 each burning warm for a moment before it cools to gray. The
//                 fifteenth pocket stays empty; a cursor blinks in it.
//   p 0.5 – 0.85  The field at night, the coffin in the middle distance. He
//                 stands by it with his head down, looks up at SITE 7, then
//                 turns and walks slowly toward the dawn behind his town.
//   p 0.85 – 1    Everything goes but one small ember of warm light, low on the
//                 left, so the middle of the frame is dark for the credits.
//
// Story beats are keyed to p so the act stretches with the timeline.

const R = (x, y, w, h) => `M ${x} ${y} h ${w} v ${h} h ${-w} Z`;
const dot = (x, y, r) => `M ${(x - r).toFixed(1)} ${y.toFixed(1)} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0 `;
const poly = (pts) => pts.map(([x, y], i) => `${i ? "L" : "M"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
const f1 = (v) => Math.round(v * 10) / 10;
const cub = ([x0, y0], [x1, y1], [x2, y2], [x3, y3], u) => {
  const v = 1 - u;
  return [
    v * v * v * x0 + 3 * v * v * u * x1 + 3 * v * u * u * x2 + u * u * u * x3,
    v * v * v * y0 + 3 * v * v * u * y1 + 3 * v * u * u * y2 + u * u * u * y3,
  ];
};

// ---------------------------------------------------------------------------
// The coffin: a plain pine toe-pincher, wide at the shoulders and narrow at
// head and foot, on two sawhorses. Drawn about the middle of its footprint
// on the ground and seen a little from above and to the left, so the lid
// shows its six sides and the hammer can rest on it.
// ---------------------------------------------------------------------------

const CO = (() => {
  const L = 380;
  const SH = 88; // sawhorse height
  const HB = 58; // box height
  const LID = 7;
  const KX = 0.18;
  const KY = 0.3;
  const S = 0.27 * L; // the shoulders
  const half = (u, inset = 0) => (u <= S ? lerp(35, 58, u / S) : lerp(58, 26, (u - S) / (L - S))) - inset;
  // plan (u along the box from the head, v across it toward the far side,
  // h up from the box's floor) to the drawing
  const P = (u, v, h) => [u - L / 2 + v * KX, -(SH + h) - v * KY];
  const us = [0, S, L];
  const near = (h, inset) => us.map((u) => P(u, -half(u, inset), h));
  const far = (h, inset) => us.map((u) => P(u, half(u, inset), h)).reverse();
  const horses = [0.19 * L, 0.8 * L];
  const leg = (u, v, dir) => {
    const [x0, y0] = P(u + dir * 7, v, 0);
    const [x1, y1] = P(u + dir * 32, v, -SH);
    return `M ${f1(x0)} ${f1(y0)} L ${f1(x1)} ${f1(y1)} `;
  };
  const brace = (u, v) => {
    const [x0, y0] = P(u - 23, v, -SH * 0.62);
    const [x1, y1] = P(u + 23, v, -SH * 0.62);
    return `M ${f1(x0)} ${f1(y0)} L ${f1(x1)} ${f1(y1)} `;
  };
  const beam = (u) => poly([P(u - 10, -50, 0), P(u + 10, -50, 0), P(u + 10, -50, -9), P(u - 10, -50, -9)]) + " Z ";
  const seam = (h) => poly(near(h, 3)) + " ";
  return {
    lid: poly([...near(HB + LID, 0), ...far(HB + LID, 0)]) + " Z",
    band: poly([...near(HB + LID, 0), ...near(HB, 0).reverse()]) + " Z",
    side: poly([...near(HB, 3), ...near(0, 3).reverse()]) + " Z",
    head: poly([P(0, -half(0, 3), 0), P(0, half(0, 3), 0), P(0, half(0, 3), HB), P(0, -half(0, 3), HB)]) + " Z",
    headLid: poly([P(0, -half(0), HB), P(0, half(0), HB), P(0, half(0), HB + LID), P(0, -half(0), HB + LID)]) + " Z",
    seams: seam(HB / 3) + seam((2 * HB) / 3),
    crease: poly([P(S, -half(S, 3), 0), P(S, -half(S, 3), HB)]),
    grain: [-0.4, 0, 0.4].map((k) => poly([P(14, k * half(14), HB + LID), P(S, k * half(S), HB + LID), P(L - 14, k * half(L - 14), HB + LID)])).join(" "),
    nails: [4, S - 4, S + 4, L - 4].map((u) => [0.2, 0.5, 0.82].map((k) => {
      const [x, y] = P(u, -half(u, 3), HB * k);
      return dot(x, y, 1.3);
    }).join("")).join(""),
    rim: poly(far(HB + LID, 0).reverse()),
    farLegs: horses.map((u) => leg(u, 46, -1) + leg(u, 46, 1)).join(""),
    legs: horses.map((u) => leg(u, -50, -1) + leg(u, -50, 1)).join(""),
    braces: horses.map((u) => brace(u, -50)).join(""),
    beams: horses.map(beam).join(""),
    // the hammer stands on its face on the lid, toward the foot
    hammer: P(0.79 * L, 4, HB + LID),
  };
})();

// A claw hammer in profile, standing on its face with the claw up and the
// handle running back to rest its grip on the lid. Origin is the face.
const HAMMER = {
  handle: "M -6 -22 Q -50 -17 -95 -9 Q -101 -7 -99 -2.5 Q -95 0 -91 -1.2 Q -50 -10 -6 -15 Z",
  head: "M -7.5 0 Q -8 -3.5 -7 -7 L -4.5 -8 L -4.5 -13.5 L -6.5 -14.5 L -6.5 -25 Q -7 -33 -14 -38 " +
    "L -12.5 -39.8 Q 2 -38 6.5 -25 L 6.5 -14.5 L 4.5 -13.5 L 4.5 -8 L 7 -7 Q 8 -3.5 7.5 0 Z",
  grain: "M -10 -18.6 Q -50 -14 -92 -5.5",
  glint: "M 6.5 -24 L 6.5 -15 M 7.4 -6.5 Q 8.2 -3.5 7.8 -0.5",
};

function Coffin({ night = 0, rim = 1 }) {
  const wood = mixColor("#1d150f", "#050506", night);
  const [hx, hy] = CO.hammer;
  return (
    <g>
      <ellipse cx={0} cy={4} rx={250} ry={30} fill="#000000" opacity={0.55} />
      <path d={CO.farLegs} stroke={mixColor("#120d09", "#030304", night)} strokeWidth={7} strokeLinecap="square" fill="none" />
      <path d={CO.legs} stroke={wood} strokeWidth={8} strokeLinecap="square" fill="none" />
      <path d={CO.braces} stroke={wood} strokeWidth={5} fill="none" />
      <path d={CO.beams} fill={wood} />
      <path d={CO.side} fill={night > 0.5 ? "#0b0b0d" : "url(#twm-pine)"} />
      <path d={CO.head} fill={night > 0.5 ? "#09090b" : "url(#twm-pinehead)"} />
      <path d={CO.seams} stroke="#000000" strokeOpacity={0.5} strokeWidth={1.5} fill="none" />
      <path d={CO.crease} stroke="#000000" strokeOpacity={0.55} strokeWidth={2} fill="none" />
      {night < 0.5 && <path d={CO.nails} fill="#d8b07c" opacity={0.6} />}
      <path d={CO.band} fill={mixColor("#4a3524", "#09090a", night)} />
      <path d={CO.headLid} fill={mixColor("#8c6340", "#09090a", night)} />
      <path d={CO.lid} fill={night > 0.5 ? "#151d26" : "url(#twm-lid)"} />
      {night < 0.5 && <path d={CO.grain} stroke="#5e4630" strokeWidth={1} opacity={0.5} fill="none" />}
      <path d={CO.rim} stroke={night > 0.5 ? C.cold : C.warmHot} strokeWidth={1.4} opacity={night > 0.5 ? 0.5 * rim : 0.55} fill="none" />
      <g transform={`translate(${f1(hx)} ${f1(hy)}) scale(0.9)`}>
        <path d={HAMMER.handle} fill={mixColor("#7b5735", "#0b0b0d", night)} />
        <path d={HAMMER.grain} stroke={mixColor("#b48a5c", "#33414f", night)} strokeWidth={1.2} fill="none" opacity={0.8} />
        <path d={HAMMER.head} fill={mixColor("#202125", "#060709", night)} />
        <path d={HAMMER.glint} stroke={C.cold} strokeWidth={1.3} fill="none" opacity={night > 0.5 ? 0.5 * rim : 0.75} />
      </g>
    </g>
  );
}

// ---------------------------------------------------------------------------
// The funeral: the field at dusk.
// ---------------------------------------------------------------------------

const HA = 600; // horizon
const CF = { x: 760, y: 712 }; // the coffin's footprint
const DCA = { x: 1040, w: 600, h: 300, s: 0.45 };
const SOLD = { x: 1226, y: 656, h: 154 };

// His town, small on the horizon: houses, a steeple, a water tower, a grain
// elevator. Drawn about the horizon at y = 690; each shot moves it into place.
const TOWN =
  "M 150 690 V 682 L 158 676 L 166 682 V 690 Z M 171 690 V 684 h 17 V 690 Z " +
  "M 205 690 V 671 L 210 661 L 215 671 V 690 Z M 209.5 661 v -8 h 1 v 8 Z " +
  "M 226 690 V 681 L 235 675 L 244 681 V 690 Z " +
  "M 278 664 Q 290 655 302 664 V 674 H 278 Z M 289.5 655 v -5 h 1 v 5 Z " +
  "M 280 674 h 1.8 l 0.6 16 h -1.8 Z M 298.2 674 h 1.8 l -0.6 16 h -1.8 Z M 289.1 674 h 1.8 v 16 h -1.8 Z " +
  "M 330 690 V 666 h 11 V 690 Z M 341 690 V 672 h 9 V 690 Z M 330 666 l 5.5 -5 l 5.5 5 Z " +
  "M 360 690 V 684 L 367 680 L 374 684 V 690 Z";
// a few windows still lit in it at dusk
const TOWN_LIGHTS = [[158, 685], [180, 687], [235, 685], [336, 680], [345, 683], [367, 686]];

// A staked tomato plant gone over, in its own frame (base at 0, 0): three
// stems tied up the stake and falling away from it, leaves hanging limp,
// fruit split and soft. dir = 1 leans its stems right.
function tomato(seed, h, dir) {
  const r = rng(seed);
  const lean = dir * 6;
  const out = { stake: `M 0 60 L ${lean} ${-h}`, stems: "", leaves: "", dead: "", fruits: [], shine: "", rot: "", cracks: "", ties: "" };
  for (let i = 0; i < 3; i++) {
    const side = i === 1 ? -dir : dir;
    const ty = -h * (0.42 + 0.19 * i);
    const tx = lean * (-ty / h);
    out.ties += R(f1(tx - 4), f1(ty - 1.5), 8, 3);
    out.stems += `M ${side * 7} 50 C ${side * 16} ${f1(ty * 0.35)} ${-side * 9} ${f1(ty * 0.72)} ${f1(tx)} ${f1(ty)} `;
    const droop = [[tx, ty], [tx + side * 34, ty - 18], [tx + side * (70 + r() * 30), ty + 26], [tx + side * (78 + r() * 34), ty + 92 + r() * 46]];
    out.stems += `M ${droop.map(([x, y], k) => `${k === 1 ? "C " : ""}${f1(x)} ${f1(y)}`).join(" ")} `;
    for (let j = 0; j < 6; j++) {
      const [lx, ly] = cub(...droop, 0.12 + j * 0.15);
      const a = Math.PI / 2 + side * (0.15 + r() * 0.55) - side * 0.18 * j;
      const len = 26 + r() * 18;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      const edge = [];
      for (let k = 0; k <= 6; k++) {
        const u = k / 6;
        const w = Math.sin(Math.PI * u) * len * 0.3 * (k % 2 ? 1.25 : 0.8);
        edge.push([lx + ca * len * u - sa * w, ly + sa * len * u + ca * w]);
      }
      for (let k = 5; k >= 1; k--) {
        const u = k / 6;
        const w = Math.sin(Math.PI * u) * len * 0.3 * (k % 2 ? 1.25 : 0.8);
        edge.push([lx + ca * len * u + sa * w, ly + sa * len * u - ca * w]);
      }
      if (r() < 0.35) out.dead += poly(edge) + " Z ";
      else out.leaves += poly(edge) + " Z ";
    }
    for (let j = 0; j < 2; j++) {
      const [sx, sy] = cub(...droop, 0.38 + j * 0.36 + r() * 0.08);
      const stalk = 7 + r() * 9;
      const fr = 10 + r() * 5;
      const fx = sx + side * 2;
      const fy = sy + stalk + fr;
      const sag = 0.72 + r() * 0.22; // gone soft, sagging
      out.stems += `M ${f1(sx)} ${f1(sy)} L ${f1(fx)} ${f1(fy - fr * sag)} `;
      out.fruits.push({ x: f1(fx), y: f1(fy), rx: f1(fr), ry: f1(fr * sag), rot: r() < 0.4 });
      const rx = fr * (0.55 + r() * 0.2);
      const ry = fr * sag * (0.36 + r() * 0.14);
      out.rot += `M ${f1(fx - rx)} ${f1(fy + fr * sag - ry * 1.1)} a ${f1(rx)} ${f1(ry)} 0 1 0 ${f1(2 * rx)} 0 a ${f1(rx)} ${f1(ry)} 0 1 0 ${f1(-2 * rx)} 0 `;
      if (r() < 0.55) out.shine += `M ${f1(fx - fr * 0.72)} ${f1(fy - fr * sag * 0.05)} Q ${f1(fx - fr * 0.66)} ${f1(fy - fr * sag * 0.62)} ${f1(fx - fr * 0.18)} ${f1(fy - fr * sag * 0.8)} `;
      if (r() < 0.5) out.cracks += `M ${f1(fx + fr * 0.05)} ${f1(fy - fr * sag * 0.95)} L ${f1(fx + fr * 0.2)} ${f1(fy - fr * sag * 0.35)} L ${f1(fx - fr * 0.05)} ${f1(fy + fr * 0.1)} `;
    }
  }
  // two that already fell, split open on the ground
  for (let j = 0; j < 2; j++) {
    const fx = dir * (18 + j * 26) + (r() - 0.5) * 10;
    const fr = 9 + r() * 4;
    out.fruits.push({ x: f1(fx), y: 44, rx: f1(fr), ry: f1(fr * 0.5), rot: true });
    out.cracks += `M ${f1(fx - fr * 0.6)} 43 L ${f1(fx + fr * 0.5)} 45 `;
  }
  return out;
}
const VINES = [
  { x: 196, y: 840, plant: tomato(1201, 400, 1), sway: 0 },
  { x: 1392, y: 862, plant: tomato(1202, 360, -1), sway: 1.9 },
];

// Grass in three depths, redrawn every frame so gusts can roll across it.
const BLADES = (() => {
  const r = rng(707);
  const bands = [
    { n: 170, y0: HA + 2, y1: HA + 44, h0: 2, h1: 6 },
    { n: 150, y0: HA + 44, y1: 760, h0: 5, h1: 13 },
    { n: 110, y0: 760, y1: 960, h0: 14, h1: 34 },
  ];
  return bands.map((b) => Array.from({ length: b.n }, () => {
    const y = lerp(b.y0, b.y1, Math.pow(r(), 0.8));
    return { x: -320 + r() * 2240, y, h: lerp(b.h0, b.h1, r()) * (0.7 + 0.5 * (y - b.y0) / (b.y1 - b.y0)), lean: (r() - 0.5) * 0.5 };
  }));
})();

const FIREFLIES = (() => {
  const r = rng(1313);
  return Array.from({ length: 13 }, () => ({
    x: 300 + r() * 1040, y: 540 + r() * 200, ax: 14 + r() * 26, ay: 6 + r() * 12,
    rx: 0.18 + r() * 0.25, ry: 0.3 + r() * 0.3, ph: r() * 6.28, blink: 0.55 + r() * 0.6, bph: r() * 6.28,
  }));
})();

// Long thin clouds low in the sky, lit from underneath by what's left of the
// day on the left and by SITE 7 on the right.
const CLOUDS = [
  [-120, 486, 760, 7], [380, 452, 620, 5], [700, 512, 900, 6], [150, 540, 520, 4], [980, 470, 560, 4],
].map(([x, y, w, h]) => `M ${x} ${y} Q ${x + w * 0.3} ${y - h * 1.6} ${x + w * 0.62} ${y - h * 0.7} Q ${x + w * 0.86} ${y - h * 0.2} ${x + w} ${y + h * 0.6} Q ${x + w * 0.5} ${y + h * 1.4} ${x} ${y} Z`).join(" ");

const DUSK_STARS = (() => {
  const r = rng(808);
  let d = "";
  for (let i = 0; i < 40; i++) d += dot(-300 + r() * 2200, -600 + r() * 920, f1(0.6 + r() * 0.9));
  return d;
})();

function grass(band, local, amp) {
  let d = "";
  band.forEach((b) => {
    const s = amp * (Math.sin(local * 1.25 - b.x * 0.006) + 0.45 * Math.sin(local * 2.3 - b.x * 0.013 + b.y * 0.02));
    d += `M ${f1(b.x)} ${f1(b.y)} l ${f1(b.h * (b.lean + s))} ${f1(-b.h)} `;
  });
  return d;
}

function Vine({ v, local }) {
  const P = v.plant;
  const sway = 1.1 * Math.sin(local * 0.9 + v.sway) + 0.5 * Math.sin(local * 2.1 + v.sway * 2);
  return (
    <g transform={`translate(${v.x} ${v.y}) rotate(${f1(sway * 10) / 10})`}>
      <path d={P.stake} stroke="#211b15" strokeWidth={7} strokeLinecap="round" fill="none" />
      <path d={P.stake} stroke="#4a3d30" strokeWidth={1.4} fill="none" transform="translate(-2 0)" opacity={0.7} />
      <path d={P.ties} fill="#8a7f6c" opacity={0.5} />
      <path d={P.stems} stroke="#1a1a14" strokeWidth={3.2} strokeLinecap="round" fill="none" />
      <path d={P.leaves} fill="#1c1d16" />
      <path d={P.dead} fill="#2e2419" />
      {P.fruits.map((f, i) => (
        <ellipse key={i} cx={f.x} cy={f.y} rx={f.rx} ry={f.ry} fill={f.rot ? "url(#twm-rot)" : "url(#twm-tom)"} />
      ))}
      <path d={P.rot} fill="#0d0605" />
      <path d={P.shine} stroke="#6d2c17" strokeWidth={1.8} strokeLinecap="round" fill="none" opacity={0.7} />
      <path d={P.cracks} stroke="#0a0403" strokeWidth={1.8} strokeLinecap="round" fill="none" />
    </g>
  );
}

function SoldSign({ local }) {
  const { x, y, h } = SOLD;
  const swing = 2.2 * Math.sin(local * 1.1) + 0.8 * Math.sin(local * 2.6);
  const ax = x - 78;
  const ay = y - h + 12;
  return (
    <g transform={`rotate(-7 ${x} ${y})`}>
      <path d={`M ${x} ${y + 4} V ${y - h} M ${x + 3} ${ay} H ${ax}`} stroke="#120d09" strokeWidth={6} fill="none" />
      <g transform={`rotate(${f1(swing * 10) / 10} ${ax + 34} ${ay})`}>
        <path d={`M ${ax + 6} ${ay} V ${ay + 12} M ${ax + 62} ${ay} V ${ay + 12}`} stroke="#2a221a" strokeWidth={1.4} />
        <rect x={ax} y={ay + 12} width={68} height={44} fill="#c9c1b1" stroke="#16100b" strokeWidth={1.6} />
        <text
          x={ax + 34 + 1} y={ay + 36} fontFamily={FONT.mono} fontSize={19} fontWeight={500}
          letterSpacing="0.1em" textAnchor="middle" fill="#2a1d16"
        >
          SOLD
        </text>
        <path d={`M ${ax + 8} ${ay + 42} H ${ax + 60}`} stroke="#2a1d16" strokeOpacity={0.5} strokeWidth={0.8} />
        <path d={`M ${ax + 14} ${ay + 48.5} H ${ax + 54}`} stroke="#4a3a2e" strokeOpacity={0.45} strokeWidth={1.6} strokeDasharray="2.2 1.2" />
      </g>
    </g>
  );
}

function Funeral({ t, local, p }) {
  const z = 1 + 0.055 * smooth(seg(p, 0, 0.21));
  const dusk = seg(p, 0, 0.21);
  const hum = 0.9 + 0.06 * Math.sin(local * 1.6);
  return (
    <g transform={`translate(780 600) scale(${z}) translate(-780 -600)`}>
      <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={BLEED.h} fill="url(#twm-dusk)" />
      <path d={DUSK_STARS} fill="#dfe7ef" opacity={0.15 + 0.35 * dusk} />
      <ellipse cx={430} cy={HA} rx={820} ry={230} fill="url(#twm-sunset)" opacity={0.95 - 0.3 * dusk} />
      <ellipse cx={1175} cy={HA - 70} rx={560} ry={320} fill="url(#twm-coldsky)" opacity={0.75 * hum} />
      <g transform={`translate(${f1(local * 1.5)} 0)`}>
        <path d={CLOUDS} fill="url(#twm-cloud)" opacity={0.85} />
      </g>

      {/* the field */}
      <rect x={BLEED.x} y={HA} width={BLEED.w} height={BLEED.y + BLEED.h - HA} fill="url(#twm-field)" />
      <ellipse cx={1175} cy={HA + 14} rx={640} ry={70} fill="url(#twm-coldground)" opacity={0.9 * hum} />
      <ellipse cx={430} cy={HA + 6} rx={700} ry={60} fill="url(#twm-dawnground)" opacity={0.7 - 0.25 * dusk} />
      <path d={`M ${BLEED.x} ${HA} H ${BLEED.x + BLEED.w}`} stroke="#5a3c26" strokeWidth={1} opacity={0.5} />
      <g transform="translate(185 -90)">
        <path d={TOWN} fill="#060608" />
        {TOWN_LIGHTS.map(([lx, ly], i) => (
          <rect key={i} x={lx - 1} y={ly - 1} width={2} height={2} fill={C.warmHot} opacity={0.55 + 0.35 * Math.sin(local * 0.7 + i * 2.1)} />
        ))}
      </g>
      <g transform={`translate(${DCA.x} ${HA}) scale(${DCA.s})`}>
        <DataCenter id="twm-dca" x={0} y={0} w={DCA.w} h={DCA.h} glow={hum} t={t} />
      </g>
      <path d={grass(BLADES[0], local, 0.25)} stroke="#1c2024" strokeWidth={1.1} strokeLinecap="round" />
      <SoldSign local={local} />
      <path d={grass(BLADES[1], local, 0.3)} stroke="#15181a" strokeWidth={1.4} strokeLinecap="round" />

      {/* the coffin */}
      <g transform={`translate(${CF.x} ${CF.y})`}>
        <Coffin />
      </g>

      {FIREFLIES.map((f, i) => {
        const b = Math.pow(Math.max(0, Math.sin(local * f.blink + f.bph)), 6);
        if (b < 0.04) return null;
        const fx = f.x + f.ax * Math.sin(local * f.rx + f.ph);
        const fy = f.y + f.ay * Math.sin(local * f.ry + f.ph * 1.7);
        return (
          <g key={i} opacity={b * (0.6 + 0.4 * dusk)}>
            <circle cx={f1(fx)} cy={f1(fy)} r={17} fill="url(#twm-ff)" />
            <circle cx={f1(fx)} cy={f1(fy)} r={2.1} fill="#ffeac0" />
          </g>
        );
      })}

      {/* the garden in front */}
      <path d={grass(BLADES[2], local, 0.34)} stroke="#0d0f0e" strokeWidth={2} strokeLinecap="round" />
      {VINES.map((v, i) => <Vine key={i} v={v} local={local} />)}
    </g>
  );
}

// ---------------------------------------------------------------------------
// The rack: three columns of five pockets, filled top to bottom, left to
// right. The trades the spoken intro names come first. The fifteenth pocket
// stays empty.
// ---------------------------------------------------------------------------

const TRADES = [
  "TEXTILE WORKER", "AUTO WORKER", "WELDER", "MACHINIST", "COAL MINER",
  "TRUCK DRIVER", "LINEMAN", "FARMHAND", "CARPENTER", "IRONWORKER",
  "LINE COOK", "CASHIER", "CALL CENTER", "DATA ENTRY",
];

const CARD = { w: 300, h: 46, pitchX: 340, pitchY: 86 };
const RACK = { x: 310, y: 214 };
const SLOTS = Array.from({ length: 15 }, (_, i) => ({
  x: RACK.x + Math.floor(i / 5) * CARD.pitchX,
  y: RACK.y + (i % 5) * CARD.pitchY,
}));
const STAMP0 = 0.245; // p of the first stamp
const STAMP_GAP = 0.015;
const EMPTY = STAMP0 + 14 * STAMP_GAP; // p when the fifteenth would have come
const TYPE_S = 0.5; // seconds to type a name
const GRAY = "#6c6c6c";
const SIZE = 21;
const ADV = SIZE * 0.78; // mono advance plus tracking
// Real cards never sit level: each rides a little high or low in its pocket.
const JIT = (() => {
  const r = rng(1401);
  return SLOTS.map(() => ({ dy: r() * 6 - 2, rot: (r() - 0.5) * 1.1 }));
})();
const PLATE = { x: RACK.x - 36, y: RACK.y - 40, w: 2 * CARD.pitchX + CARD.w + 72, h: 5 * CARD.pitchY + 44 };

function TimeCard({ x, y, name, n, d, jit }) {
  const tx = x + CARD.w / 2 - (name.length * ADV) / 2 + SIZE * 0.09;
  const typed = d <= 0 ? 0 : Math.min(name.length, Math.ceil((d / TYPE_S) * name.length));
  const warm = d <= 0 ? 0 : d < 0.9 ? 1 : Math.exp(-(d - 0.9) * 0.7);
  const col = mixColor(GRAY, C.warmHot, warm);
  const typing = d > 0 && d < TYPE_S + 0.25;
  // the stamp lands: the card knocks down into its pocket and settles
  const clunk = d > TYPE_S ? 3 * Math.exp(-(d - TYPE_S) * 9) : 0;
  const textProps = {
    x: tx, y: y + 31, fontFamily: FONT.mono, fontSize: SIZE, fontWeight: 600,
    letterSpacing: "0.18em", textAnchor: "start",
  };
  return (
    <g transform={`translate(0 ${jit.dy + clunk}) rotate(${jit.rot} ${x + CARD.w / 2} ${y + 40})`}>
      {warm > 0.02 && (
        <ellipse cx={x + CARD.w / 2} cy={y + 24} rx={240} ry={74} fill="url(#twm-cardglow)" opacity={warm * 0.7} />
      )}
      <rect x={x} y={y} width={CARD.w} height={CARD.h + 22} fill={d > 0 ? mixColor("#161514", "#4a3421", warm * 0.85) : "#0f0f0f"} />
      <path d={`M ${x} ${y + 0.5} h ${CARD.w}`} stroke={d > 0 ? mixColor("#262422", C.warm, warm * 0.6) : "#1a1a1a"} strokeWidth={1} />
      <text x={x + 12} y={y + 15} fontFamily={FONT.mono} fontSize={9} letterSpacing="0.2em" fill={d > 0 ? "#4d4d4d" : "#262626"}>
        {String(n).padStart(2, "0")}
      </text>
      {typed > 0 && warm > 0.02 && (
        <text {...textProps} fill="none" stroke={C.warm} strokeWidth={6} strokeLinejoin="round" opacity={warm * 0.28}>
          {name.slice(0, typed)}
        </text>
      )}
      {typed > 0 && <text {...textProps} fill={col}>{name.slice(0, typed)}</text>}
      {typing && <rect x={tx + typed * ADV} y={y + 15} width={SIZE * 0.55} height={SIZE * 0.85} fill={C.warmHot} opacity={0.85} />}
    </g>
  );
}

function Wall({ local, dur, p }) {
  // a slow drift in, settling on the empty pocket at the end
  const z = 1 + 0.05 * seg(p, 0.215, 0.5);
  const fx = lerp(800, 880, smooth(seg(p, 0.4, 0.5)));
  const fy = lerp(420, 450, smooth(seg(p, 0.4, 0.5)));
  const last = SLOTS[14];
  const waiting = local - EMPTY * dur;
  const cursor = waiting > 0.3 && Math.floor((waiting - 0.3) * 1.6) % 2 === 0;
  return (
    <g transform={`translate(800 420) scale(${z}) translate(${-fx} ${-fy})`}>
      <ellipse cx={800} cy={250} rx={820} ry={430} fill="url(#twm-toplight)" />
      <rect x={PLATE.x} y={PLATE.y} width={PLATE.w} height={PLATE.h} fill="#0a0a0a" stroke="#161616" strokeWidth={1.5} />
      {[[PLATE.x + 12, PLATE.y + 12], [PLATE.x + PLATE.w - 12, PLATE.y + 12], [PLATE.x + 12, PLATE.y + PLATE.h - 12], [PLATE.x + PLATE.w - 12, PLATE.y + PLATE.h - 12]].map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r={2.5} fill="#1c1c1c" />
      ))}
      <text
        x={800} y={PLATE.y - 22} fontFamily={FONT.mono} fontSize={12} letterSpacing="0.6em"
        textAnchor="middle" fill="#3a3a3a"
      >
        OUT
      </text>
      {SLOTS.map((sl, i) => (
        <g key={i}>
          {i < TRADES.length && (
            <TimeCard x={sl.x} y={sl.y} name={TRADES[i]} n={i + 1} jit={JIT[i]} d={local - (STAMP0 + i * STAMP_GAP) * dur} />
          )}
          <rect x={sl.x - 8} y={sl.y + 36} width={CARD.w + 16} height={28} fill="#131313" />
          <path d={`M ${sl.x - 8} ${sl.y + 36.75} h ${CARD.w + 16}`} stroke="#2c2c2c" strokeWidth={1.5} />
          <path d={`M ${sl.x - 8} ${sl.y + 63.5} h ${CARD.w + 16}`} stroke="#070707" strokeWidth={1} />
        </g>
      ))}
      {/* nobody's name comes for the last pocket; the cursor waits in it */}
      {cursor && (
        <rect x={last.x + CARD.w / 2 - SIZE * 0.28} y={last.y + 15} width={SIZE * 0.55} height={SIZE * 0.85} fill={C.warmHot} opacity={0.75} />
      )}
    </g>
  );
}

// ---------------------------------------------------------------------------
// The field at night. Horizon low so he stands against the sky; the data
// center on the right, his town small and dark on the left horizon where the
// dawn comes up. The coffin waits in the middle distance between him and it.
// ---------------------------------------------------------------------------

const H = 690;
const DCF = { x: 900, w: 600, h: 300, s: 1.4 };
const EMBER = { x: 255, y: H };
const START = { x: 700, y: 732, s: 0.5 };
const CB = { x: 806, y: 738, s: 0.24 }; // the coffin, at his scale
const DEPTH = 0.0005; // how fast he shrinks per unit walked, heading into the distance
const LOOK = 0.578; // p: head up, to SITE 7
const TURN = 0.655; // p: he turns away
const WALK = 0.675; // p: and walks
const REACH = 0.93; // p by which he has walked GO units toward the ember
const GO = 380;

const GRASS = (() => {
  const r = rng(616);
  let d = "";
  for (let i = 0; i < 260; i++) {
    const y = H + 8 + Math.pow(r(), 0.7) * 260;
    const x = -300 + r() * 2200;
    const h = (3 + r() * 9) * (0.4 + (y - H) / 200);
    d += `M ${x.toFixed(1)} ${y.toFixed(1)} l ${(r() * 4 - 2).toFixed(1)} ${(-h).toFixed(1)} `;
  }
  return d;
})();

const STARS = (() => {
  const r = rng(224);
  return Array.from({ length: 70 }, () => ({
    x: -300 + r() * 2200, y: -700 + r() * 1250, r: 0.6 + r() * 1.1,
    o: 0.25 + r() * 0.6, ph: r() * 6.28, rate: 0.4 + r() * 1.3,
  }));
})();

// Where he is and what he's doing: head down by the coffin, then looking up
// at SITE 7, then turning and walking for the horizon. Each change of pose
// blends over a fraction of a second (kit mixPose). The walk
// rate is solved so his feet keep pace with the ground at every scale.
function man(local, dur) {
  const tLook = LOOK * dur;
  const tTurn = TURN * dur;
  const tWalk = WALK * dur;
  const T = (REACH - WALK) * dur;
  const c = -Math.log(1 - (GO * DEPTH) / START.s) / (DEPTH * T); // ground speed at s = 1
  const rate = c / 21.3; // kit walk: ~21.3 units of ground per radian of phase at s = 1
  const turnFacing = (k) => {
    const f = Math.cos(Math.PI * smooth(clamp(k)));
    return (f >= 0 ? 1 : -1) * Math.max(0.24, Math.abs(f));
  };
  let u = 0;
  let pose = "slump";
  let facing = 1;
  if (local >= tWalk) {
    const w = local - tWalk;
    u = (START.s / DEPTH) * (1 - Math.exp(-c * DEPTH * w));
    pose = mixPose("stand", "walk", smooth(clamp(w / 0.35)), w * rate);
    facing = -1;
  } else if (local >= tTurn) {
    pose = mixPose("lookUp", "stand", smooth(clamp((local - tTurn) / 0.4)));
    facing = turnFacing((local - tTurn) / 1.1);
  } else if (local >= tLook) {
    pose = mixPose("slump", "lookUp", smooth(clamp((local - tLook) / 0.7)));
  }
  return { x: START.x - u, y: START.y - 0.06 * u, s: START.s - DEPTH * u, pose, facing };
}

function Field({ t, local, dur, p }) {
  const reveal = smooth(seg(p, LOOK - 0.004, LOOK + 0.07));
  const z = lerp(1.75, 1, reveal) * (1 + 0.045 * seg(p, 0.66, 0.92));
  // close: (748, 640) at the middle of the screen; wide: zoomed about (640, 640)
  const ax = lerp(800, 640, reveal);
  const ay = lerp(450, 640, reveal);
  const fx = lerp(748, 640, reveal);
  const fy = lerp(640, 640, reveal);
  const dawn = smooth(seg(p, 0.56, 0.86));
  const fade = smooth(seg(p, 0.85, 0.97));
  const hum = 1 + 0.05 * Math.sin(local * 1.6);
  const m = man(local, dur);
  const into = smooth(seg(p, 0.87, REACH)); // he walks into the light and is gone
  const ember = 0.8 + 0.2 * Math.sin(local * 0.9) * Math.sin(local * 0.37 + 1);
  const shx = 2.3;
  const skew = (x, y) => `translate(${x} ${y}) matrix(1 0 ${shx} -0.2 0 0) translate(${-x} ${-y})`;
  const figure = (color, o) => (
    <Worker x={m.x} y={m.y} s={m.s} pose={m.pose} facing={m.facing} hat="cap" color={color} opacity={o} />
  );
  const coffin = <g transform={`translate(${CB.x} ${CB.y}) scale(${CB.s})`}><Coffin night={1} rim={hum} /></g>;

  return (
    <g transform={`translate(${f1(ax)} ${f1(ay)}) scale(${f1(z * 1000) / 1000}) translate(${-f1(fx)} ${-f1(fy)})`}>
      {/* sky */}
      <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={BLEED.h} fill="url(#twm-sky)" />
      {STARS.map((s, i) => (
        <circle
          key={i} cx={s.x} cy={s.y} r={s.r} fill="#dfe7ef"
          opacity={s.o * (0.65 + 0.35 * Math.sin(local * s.rate + s.ph)) * clamp((H - 120 - s.y) / 260) * (1 - 0.5 * dawn)}
        />
      ))}
      <ellipse cx={1300} cy={430} rx={860} ry={560} fill="url(#twm-coldsky)" opacity={0.75 * hum * (1 - 0.25 * dawn) * (1 - fade)} />
      <ellipse cx={EMBER.x} cy={EMBER.y} rx={460 + 640 * dawn} ry={170 + 290 * dawn} fill="url(#twm-dawn)" opacity={dawn} />

      {/* ground */}
      <rect x={BLEED.x} y={H} width={BLEED.w} height={BLEED.y + BLEED.h - H} fill="url(#twm-ground)" />
      <ellipse cx={1150} cy={H + 10} rx={1150} ry={170} fill="url(#twm-coldground)" opacity={hum * (1 - 0.3 * dawn) * (1 - fade)} />
      <ellipse cx={EMBER.x} cy={EMBER.y + 4} rx={520 * dawn + 60} ry={70 * dawn + 10} fill="url(#twm-dawnground)" opacity={dawn} />
      <path d={GRASS} stroke="#1a2330" strokeWidth={1.3} strokeLinecap="round" opacity={0.7} />
      <path d={`M ${BLEED.x} ${H} H ${BLEED.x + BLEED.w}`} stroke={mixColor("#1b2735", "#6b4526", dawn * 0.6)} strokeWidth={1} opacity={0.8} />

      {/* the town he came from, and the data center */}
      <path d={TOWN} fill="#050608" />
      <g transform={`translate(${DCF.x} ${H}) scale(${DCF.s})`} opacity={1 - fade}>
        <DataCenter id="twm-dc" x={0} y={0} w={DCF.w} h={DCF.h} glow={hum} t={t} />
      </g>

      {/* the coffin and him, and the long shadows the machine throws */}
      <g transform={skew(CB.x, CB.y)} opacity={0.6}>
        <g transform={`translate(${CB.x} ${CB.y}) scale(${CB.s})`}>
          <path d={CO.lid + CO.band + CO.side + CO.legs} fill="#000000" stroke="#000000" strokeWidth={8} />
        </g>
      </g>
      {coffin}
      <g transform={skew(m.x, m.y)} opacity={0.75 * (1 - into)}>
        {figure("#000000", 1)}
      </g>
      {figure("#000000", 1 - into)}

      {/* the fade: everything goes but the ember */}
      {fade > 0 && (
        <>
          <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={BLEED.h} fill="#000000" opacity={fade * 0.96} />
          <ellipse cx={EMBER.x} cy={EMBER.y} rx={220} ry={72} fill="url(#twm-dawn)" opacity={fade * 0.85 * ember} />
          <ellipse cx={EMBER.x} cy={EMBER.y - 1} rx={54} ry={12} fill="url(#twm-ember)" opacity={fade * ember} />
          <path d={TOWN} fill="#000000" opacity={fade} />
          {figure("#000000", fade * (1 - into))}
        </>
      )}
      <path d={`M ${EMBER.x - 12} ${EMBER.y} h 24`} stroke={C.warmHot} strokeWidth={1.5} strokeLinecap="round" opacity={dawn * (0.5 + 0.5 * ember)} />
    </g>
  );
}

export default function TheWorkingMan({ t, local, dur, p }) {
  // Dips to black between the three places.
  const funeralK = 1 - smooth(seg(p, 0.188, 0.21));
  const wallK = span(p, 0.214, 0.236, 0.478, 0.498);
  const fieldK = smooth(seg(p, 0.5, 0.53));
  return (
    <g>
      <defs>
        <linearGradient id="twm-dusk" gradientUnits="userSpaceOnUse" x1="0" y1="-700" x2="0" y2={HA}>
          <stop offset="0" stopColor="#020306" />
          <stop offset="0.62" stopColor="#080b12" />
          <stop offset="0.88" stopColor="#161822" />
          <stop offset="1" stopColor="#3a2b27" />
        </linearGradient>
        <radialGradient id="twm-sunset">
          <stop offset="0" stopColor={C.warm} stopOpacity="0.55" />
          <stop offset="0.35" stopColor={C.warmDeep} stopOpacity="0.32" />
          <stop offset="1" stopColor={C.warmDeep} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="twm-field" gradientUnits="userSpaceOnUse" x1="0" y1={HA} x2="0" y2="980">
          <stop offset="0" stopColor="#14120f" />
          <stop offset="0.3" stopColor="#0a0a09" />
          <stop offset="1" stopColor="#030303" />
        </linearGradient>
        <linearGradient id="twm-pine" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#86613f" />
          <stop offset="0.55" stopColor="#5d4632" />
          <stop offset="1" stopColor="#3a3634" />
        </linearGradient>
        <radialGradient id="twm-tom" cx="0.36" cy="0.3">
          <stop offset="0" stopColor="#7e3a26" />
          <stop offset="0.65" stopColor="#4e1f12" />
          <stop offset="1" stopColor="#24100a" />
        </radialGradient>
        <radialGradient id="twm-rot" cx="0.4" cy="0.35">
          <stop offset="0" stopColor="#4a301c" />
          <stop offset="1" stopColor="#1d100a" />
        </radialGradient>
        <linearGradient id="twm-cloud" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="1500" y2="0">
          <stop offset="0" stopColor="#5a3826" />
          <stop offset="0.35" stopColor="#2c2125" />
          <stop offset="0.65" stopColor="#1a1d26" />
          <stop offset="1" stopColor="#2b3b4f" />
        </linearGradient>
        <linearGradient id="twm-pinehead" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#c08c5a" />
          <stop offset="1" stopColor="#7d5636" />
        </linearGradient>
        <linearGradient id="twm-lid" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#b78f60" />
          <stop offset="0.6" stopColor="#8a7258" />
          <stop offset="1" stopColor="#6c7078" />
        </linearGradient>
        <radialGradient id="twm-ff">
          <stop offset="0" stopColor={C.warmHot} stopOpacity="0.8" />
          <stop offset="0.35" stopColor={C.warm} stopOpacity="0.25" />
          <stop offset="1" stopColor={C.warm} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="twm-toplight">
          <stop offset="0" stopColor="#232323" stopOpacity="0.6" />
          <stop offset="0.6" stopColor="#141414" stopOpacity="0.25" />
          <stop offset="1" stopColor="#0a0a0a" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="twm-cardglow">
          <stop offset="0" stopColor={C.warm} stopOpacity="0.32" />
          <stop offset="0.5" stopColor={C.warmDeep} stopOpacity="0.12" />
          <stop offset="1" stopColor={C.warmDeep} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="twm-sky" gradientUnits="userSpaceOnUse" x1="0" y1="-700" x2="0" y2={H}>
          <stop offset="0" stopColor="#010102" />
          <stop offset="0.65" stopColor="#04060a" />
          <stop offset="1" stopColor="#0c1118" />
        </linearGradient>
        <radialGradient id="twm-coldsky">
          <stop offset="0" stopColor={C.cold} stopOpacity="0.42" />
          <stop offset="0.4" stopColor={C.accent} stopOpacity="0.16" />
          <stop offset="1" stopColor={C.coldDeep} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="twm-dawn">
          <stop offset="0" stopColor={C.warmHot} stopOpacity="0.95" />
          <stop offset="0.1" stopColor={C.warm} stopOpacity="0.6" />
          <stop offset="0.4" stopColor={C.warmDeep} stopOpacity="0.25" />
          <stop offset="1" stopColor={C.warmDeep} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="twm-ember">
          <stop offset="0" stopColor="#fff1dc" stopOpacity="1" />
          <stop offset="0.35" stopColor={C.warmHot} stopOpacity="0.8" />
          <stop offset="1" stopColor={C.warm} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="twm-ground" gradientUnits="userSpaceOnUse" x1="0" y1={H} x2="0" y2="1000">
          <stop offset="0" stopColor="#090c11" />
          <stop offset="1" stopColor="#020203" />
        </linearGradient>
        <radialGradient id="twm-coldground">
          <stop offset="0" stopColor={C.cold} stopOpacity="0.32" />
          <stop offset="0.5" stopColor={C.accent} stopOpacity="0.1" />
          <stop offset="1" stopColor={C.coldDeep} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="twm-dawnground">
          <stop offset="0" stopColor={C.warm} stopOpacity="0.4" />
          <stop offset="1" stopColor={C.warmDeep} stopOpacity="0" />
        </radialGradient>
      </defs>

      <rect x={BLEED.x} y={BLEED.y} width={BLEED.w} height={BLEED.h} fill="#030303" />
      {funeralK > 0 && (
        <g opacity={funeralK}>
          <Funeral t={t} local={local} p={p} />
        </g>
      )}
      {wallK > 0 && (
        <g opacity={wallK}>
          <Wall local={local} dur={dur} p={p} />
        </g>
      )}
      {fieldK > 0 && (
        <g opacity={fieldK}>
          <Field t={t} local={local} dur={dur} p={p} />
        </g>
      )}
    </g>
  );
}
